import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, customerApi } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

const getCartId = () => {
  let id = localStorage.getItem("zebaa_cart_id");
  if (!id) {
    id = "cart_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("zebaa_cart_id", id);
  }
  return id;
};

export const CartProvider = ({ children }) => {
  const { customer } = useCustomerAuth();
  const [cartId] = useState(getCartId);
  const [cart, setCart] = useState({ items: [], subtotal: 0, count: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/cart/${cartId}`);
      setCart(data);
    } catch (e) {
      console.error("cart fetch failed", e);
    }
  }, [cartId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Wishlist requires an account: load it from the DB when signed in, empty otherwise.
  useEffect(() => {
    if (customer === null) return; // still checking
    if (customer) {
      customerApi()
        .get("/account/wishlist")
        .then(({ data }) => setWishlist(data.product_ids || []))
        .catch(() => {});
    } else {
      setWishlist([]);
    }
  }, [customer]);

  const addToCart = async (product, quantity = 1, variant = {}, openDrawer = true) => {
    try {
      const { data } = await api.post(`/cart/${cartId}/items`, {
        product_id: product.id,
        quantity,
        colour: variant.colour ?? null,
        size: variant.size ?? null,
        customization: variant.customization ?? null,
        custom_confirmed: variant.custom_confirmed ?? false,
      });
      setCart(data);
      toast.success(`${product.name} added to bag`);
      if (openDrawer) setDrawerOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to bag");
    }
  };

  const updateItem = async (lineId, quantity) => {
    const { data } = await api.put(`/cart/${cartId}/items/${encodeURIComponent(lineId)}`, { quantity });
    setCart(data);
  };

  const removeItem = async (lineId) => {
    const { data } = await api.delete(`/cart/${cartId}/items/${encodeURIComponent(lineId)}`);
    setCart(data);
  };

  const clearLocalCart = () => setCart({ items: [], subtotal: 0, count: 0 });

  const toggleWishlist = (product) => {
    if (!customer) {
      toast("Sign in to save items to your wishlist", {
        action: { label: "Sign In", onClick: () => { window.location.href = "/login"; } },
        cancel: { label: "Register", onClick: () => { window.location.href = "/register"; } },
      });
      return;
    }
    const exists = wishlist.includes(product.id);
    const next = exists
      ? wishlist.filter((i) => i !== product.id)
      : [...wishlist, product.id];
    setWishlist(next);
    if (exists) customerApi().delete(`/account/wishlist/${product.id}`).catch(() => {});
    else customerApi().post("/account/wishlist", { product_id: product.id }).catch(() => {});
    if (exists) toast.message(`${product.name} removed from wishlist`);
    else toast.success(`${product.name} added to wishlist`);
  };

  return (
    <CartContext.Provider
      value={{
        cartId,
        cart,
        refresh,
        addToCart,
        updateItem,
        removeItem,
        clearLocalCart,
        drawerOpen,
        setDrawerOpen,
        wishlist,
        toggleWishlist,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
